var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { collection, doc, getDoc, getDocs, getFirestore, query, where, updateDoc, serverTimestamp, orderBy, deleteDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isRestApiMode, apiClient, ApiError } from './restApi';
export class LocationService {
    constructor() {
        this.db = getFirestore();
        this.functions = getFunctions(undefined, 'europe-west3');
    }
    static getInstance() {
        if (!LocationService.instance) {
            LocationService.instance = new LocationService();
        }
        return LocationService.instance;
    }
    // Получение публичных локаций
    fetchPublicLocations() {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                return apiClient.get('/v1/locations');
            }
            const locationsQuery = query(collection(this.db, 'locations'), where('status', '==', 'public'));
            const snapshot = yield getDocs(locationsQuery);
            return snapshot.docs.map(doc => (Object.assign({ id: doc.id }, doc.data())));
        });
    }
    // Получение локации по ID
    getLocationById(locationId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                try {
                    return yield apiClient.get(`/v1/locations/${locationId}`);
                }
                catch (e) {
                    if (e instanceof ApiError && e.status === 404)
                        return null;
                    throw e;
                }
            }
            const locationRef = doc(this.db, 'locations', locationId);
            const locationDoc = yield getDoc(locationRef);
            if (locationDoc.exists()) {
                return Object.assign({ id: locationDoc.id }, locationDoc.data());
            }
            else {
                return null;
            }
        });
    }
    // Получение офисов для локации
    fetchOfficesForLocation(locationId, onlyActive = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                const params = { locationId };
                if (!onlyActive)
                    params.includeInactive = 'true';
                return apiClient.get('/v1/offices', params);
            }
            console.log('Fetching offices for location:', locationId);
            let officesQuery = query(collection(this.db, 'offices'), where('locationId', '==', locationId));
            if (onlyActive) {
                officesQuery = query(officesQuery, where('is_active', '==', true));
            }
            const snapshot = yield getDocs(officesQuery);
            console.log('Found offices:', snapshot.size);
            const offices = snapshot.docs.map(doc => (Object.assign({ id: doc.id }, doc.data())));
            console.log('Mapped offices:', offices);
            return offices;
        });
    }
    // Preview-only: получить минимальные данные объявления Avito по полному path или slug/id
    getAvitoPublicItem(pathOrParams) {
        return __awaiter(this, void 0, void 0, function* () {
            let payload;
            if (typeof pathOrParams === 'string') {
                // Сохраняем обратную совместимость: если пришла строка — считаем это slugOrId
                payload = { slugOrId: pathOrParams };
            }
            else {
                payload = pathOrParams;
            }
            if (isRestApiMode()) {
                const id = payload.path || payload.slugOrId || '';
                const params = {};
                if (payload.path)
                    params.path = payload.path;
                if (payload.slugOrId)
                    params.slugOrId = payload.slugOrId;
                if (payload.forceRefresh !== undefined)
                    params.forceRefresh = String(payload.forceRefresh);
                return apiClient.get(`/v1/public/avito/items/${encodeURIComponent(id)}`, params);
            }
            const fn = httpsCallable(this.functions, 'getAvitoPublicItem');
            const res = yield fn(payload);
            return res.data;
        });
    }
    // Создание запроса на резидентство
    createResidencyRequest(locationId, userId, reason, userInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                return apiClient.post(`/v1/locations/${locationId}/residency-requests`, { userId, reason, userInfo });
            }
            const createResidencyRequestFn = httpsCallable(this.functions, 'createResidencyRequest');
            const result = yield createResidencyRequestFn({
                locationId,
                userId,
                reason,
                userInfo
            });
            return result.data;
        });
    }
    // Получение запроса на резидентство пользователя для локации
    getUserResidencyRequest(locationId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                try {
                    const results = yield apiClient.get(`/v1/locations/${locationId}/residency-requests`, { userId });
                    return results.length > 0 ? results[0] : null;
                }
                catch (e) {
                    if (e instanceof ApiError && e.status === 404)
                        return null;
                    throw e;
                }
            }
            const requestsQuery = query(collection(this.db, 'locations', locationId, 'residencyRequests'), where('userId', '==', userId), orderBy('createdAt', 'desc'));
            const snapshot = yield getDocs(requestsQuery);
            if (snapshot.empty) {
                return null;
            }
            // Берем самый последний запрос
            const doc = snapshot.docs[0];
            return Object.assign({ id: doc.id }, doc.data());
        });
    }
    // Получение всех запросов на резидентство для локации
    getLocationResidencyRequests(locationId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                return apiClient.get(`/v1/locations/${locationId}/residency-requests`, { status: 'pending' });
            }
            const requestsQuery = query(collection(this.db, 'locations', locationId, 'residencyRequests'), where('status', '==', 'pending'), orderBy('createdAt', 'desc'));
            const snapshot = yield getDocs(requestsQuery);
            return snapshot.docs.map(doc => (Object.assign({ id: doc.id }, doc.data())));
        });
    }
    // Проверка статуса пользователя в локации
    getUserStatusInLocation(locationId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                // Use the /users/me endpoint which requires only auth (not locationAdmin).
                // All callers pass the current user's own uid, so this is always a self-lookup.
                try {
                    return yield apiClient.get(`/v1/locations/${locationId}/users/me`);
                }
                catch (err) {
                    // 404 means the user has no membership record — treat as null
                    if (err && typeof err === 'object' && 'status' in err && err.status === 404) {
                        return null;
                    }
                    throw err;
                }
            }
            const userStatusQuery = query(collection(this.db, 'locationUsers'), where('locationId', '==', locationId), where('userId', '==', userId));
            const snapshot = yield getDocs(userStatusQuery);
            if (snapshot.empty) {
                return null;
            }
            return Object.assign({}, snapshot.docs[0].data());
        });
    }
    // Получение локаций, где пользователь имеет определенную роль
    getLocationsByUserRole(userId, role) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                const allLocations = yield apiClient.get('/v1/users/me/locations');
                return allLocations.filter(l => l.role === role);
            }
            const userStatusQuery = query(collection(this.db, 'locationUsers'), where('userId', '==', userId), where('role', '==', role));
            const snapshot = yield getDocs(userStatusQuery);
            if (snapshot.empty) {
                return [];
            }
            const locationIds = snapshot.docs.map(doc => doc.data().locationId);
            const locations = [];
            for (const locationId of locationIds) {
                const location = yield this.getLocationById(locationId);
                if (location) {
                    locations.push(location);
                }
            }
            return locations;
        });
    }
    // Получение всех пользователей локации
    getLocationUsers(locationId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                return apiClient.get(`/v1/locations/${locationId}/users`);
            }
            const usersQuery = query(collection(this.db, 'locationUsers'), where('locationId', '==', locationId));
            const snapshot = yield getDocs(usersQuery);
            return snapshot.docs.map(doc => (Object.assign(Object.assign({}, doc.data()), { userId: doc.id.split('_')[0] // Extract userId from document ID
             })));
        });
    }
    // Проверяет, является ли пользователь администратором локации
    isLocationAdmin(userId, locationId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                try {
                    const locationUser = yield apiClient.get(`/v1/locations/${locationId}/users/me`);
                    return locationUser.role === 'admin';
                }
                catch (e) {
                    if (e instanceof ApiError && e.status === 404)
                        return false;
                    throw e;
                }
            }
            const userDocId = `${userId}_${locationId}`;
            const userDoc = yield getDoc(doc(this.db, 'locationUsers', userDocId));
            if (!userDoc.exists) {
                return false;
            }
            const userData = userDoc.data();
            if (!userData) {
                return false;
            }
            return userData.role === 'admin';
        });
    }
    // Получение локации по домену
    findLocationByDomain(domain) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!domain)
                return null;
            if (isRestApiMode()) {
                try {
                    return yield apiClient.get('/v1/locations/resolve', { domain: domain.toLowerCase() });
                }
                catch (e) {
                    if (e instanceof ApiError && e.status === 404)
                        return null;
                    throw e;
                }
            }
            console.log('[LocationService] Searching for location with domain:', domain);
            // Ищем в коллекции domains
            const domainDoc = yield getDoc(doc(this.db, 'domains', domain.toLowerCase()));
            if (!domainDoc.exists()) {
                console.log('[LocationService] No domain mapping found for:', domain);
                return null;
            }
            const domainMapping = domainDoc.data();
            console.log('[LocationService] Found domain mapping:', domainMapping);
            // Получаем локацию по ID из маппинга
            const location = yield this.getLocationById(domainMapping.locationId);
            console.log('[LocationService] Retrieved location:', location);
            return location;
        });
    }
    // Сохранение маппинга домена
    setDomainMapping(domain, locationId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                yield apiClient.post(`/v1/locations/${locationId}/domains`, { domain: domain.toLowerCase() });
                return;
            }
            const domainRef = doc(this.db, 'domains', domain.toLowerCase());
            yield setDoc(domainRef, {
                domain: domain.toLowerCase(),
                locationId,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        });
    }
    // Удаление маппинга домена
    deleteDomainMapping(domain) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                yield apiClient.delete(`/v1/locations/domains/${encodeURIComponent(domain.toLowerCase())}`);
                return;
            }
            yield deleteDoc(doc(this.db, 'domains', domain.toLowerCase()));
        });
    }
    // Получение всех доменов для локации
    getLocationDomains(locationId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                return apiClient.get(`/v1/locations/${locationId}/domains`);
            }
            const domainsQuery = query(collection(this.db, 'domains'), where('locationId', '==', locationId));
            const snapshot = yield getDocs(domainsQuery);
            return snapshot.docs.map(doc => (Object.assign({}, doc.data())));
        });
    }
    // Получение маппинга домена
    getDomainMapping(domain) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                try {
                    return yield apiClient.get('/v1/locations/resolve', { domain: domain.toLowerCase() });
                }
                catch (e) {
                    if (e instanceof ApiError && e.status === 404)
                        return null;
                    throw e;
                }
            }
            const domainDoc = yield getDoc(doc(this.db, 'domains', domain.toLowerCase()));
            if (!domainDoc.exists()) {
                return null;
            }
            return domainDoc.data();
        });
    }
    // Создание новой локации
    createLocation(locationData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                return apiClient.post('/v1/locations', {
                    name: locationData.name,
                    address: locationData.address,
                    timezone: locationData.timezone,
                    latitude: locationData.latitude,
                    longitude: locationData.longitude,
                });
            }
            const createLocationFn = httpsCallable(this.functions, 'createLocation');
            const result = yield createLocationFn({
                name: locationData.name,
                address: locationData.address,
                timezone: locationData.timezone,
                latitude: locationData.latitude,
                longitude: locationData.longitude
            });
            return result.data;
        });
    }
    // Обновление роли пользователя в локации
    updateUserRole(locationId, userId, role) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                yield apiClient.patch(`/v1/locations/${locationId}/users/${userId}`, { role });
                return;
            }
            const userDocId = `${userId}_${locationId}`;
            const userRef = doc(this.db, 'locationUsers', userDocId);
            yield updateDoc(userRef, {
                role,
                updatedAt: serverTimestamp()
            });
        });
    }
    // Обновление информации о пользователе в локации
    updateUserInfo(locationId, userId, userInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                yield apiClient.patch(`/v1/locations/${locationId}/users/${userId}`, userInfo);
                return;
            }
            const userDocId = `${userId}_${locationId}`;
            const userRef = doc(this.db, 'locationUsers', userDocId);
            yield updateDoc(userRef, Object.assign(Object.assign({}, userInfo), { updatedAt: serverTimestamp() }));
        });
    }
    // Удаление пользователя из локации
    removeUser(locationId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRestApiMode()) {
                yield apiClient.delete(`/v1/locations/${locationId}/users/${userId}`);
                return;
            }
            const userDocId = `${userId}_${locationId}`;
            const userRef = doc(this.db, 'locationUsers', userDocId);
            yield deleteDoc(userRef);
        });
    }
}
